(function () {
  if (customElements.get('banner-slider')) return;

  class BannerSlider extends HTMLElement {
    connectedCallback() {
      this.flkty = null;
      const track = this.querySelector('.banner-slider__track');
      if (!track || track.children.length <= 1) return;

      this.cleanupBreakpoint = window.SliderSettings.onBreakpointChange(() => {
        this.destroySlider();
        this.init();
      });

      if (typeof Flickity === 'undefined') {
        this._onLoad = () => this.init();
        window.addEventListener('load', this._onLoad);
        return;
      }
      this.init();
    }

    init() {
      const track = this.querySelector('.banner-slider__track');
      if (!track || typeof Flickity === 'undefined' || this.flkty) return;

      // Banner is a hero with fixed UX — wrapAround stays true, pageDots stay on,
      // arrows stay off, and autoplay is always on (a hero that doesn't rotate
      // would be unusual). Only the autoplay *interval* (per-section) and
      // drag/swipe/transition honour the globals.
      //
      // Strict 1:1 paging is always forced: the global mobile swipe mode defaults
      // to "free", which turns on Flickity's freeScroll. Combined with the hero's
      // full-width slides (and wrapAround) a swipe then comes to rest wherever the
      // momentum ends — leaving two half-visible banners instead of snapping to
      // one. groupCells/freeScroll off keeps every swipe a clean one-slide step.
      // The options alone only guarantee it rests *on* a slide, not on the
      // adjacent one — see _forceSingleStepPaging below.
      const interval = parseInt(this.dataset.autoplayInterval, 10) || 5;
      const overrides = {
        wrapAround: true,
        contain: false,
        prevNextButtons: false,
        autoPlay: interval * 1000,
        groupCells: false,
        freeScroll: false,
        // CFSN-640: the settle spring must be aperiodic for a full-width hero.
        // The global pairing (selectedAttraction 0.025 from transitionDuration 400,
        // friction 0.20 on mobile) is underdamped: simulated over one slide width it
        // overshoots 17% with leftover drag momentum and still 6% from a standstill,
        // and takes ~900ms to come to rest instead of the configured 400ms. That
        // overshoot is what briefly shows the *next* slide before springing back.
        // 0.085/0.49 settles in ~350ms with no overshoot, with or without momentum.
        // Raising friction also shrinks Flickity's momentum projection
        // (getRestingPosition = x + velocity/friction) from velocity x5 to x2.
        selectedAttraction: 0.085,
        friction: 0.49,
      };
      const opts = window.getFlickityOptions(this, { pageDots: true }, overrides);

      this.flkty = new Flickity(track, opts);
      this._forceSingleStepPaging();

      this.flkty.on('change', () => this._syncTabindex());
      this._syncTabindex();

      this.addEventListener('focusin', () => this.flkty?.pausePlayer());
      this.addEventListener('focusout', () => this.flkty?.unpausePlayer());
    }

    // CFSN-640: groupCells/freeScroll off only guarantees the slider comes to rest
    // *on* a slide — not on the adjacent one. Flickity's dragEndRestingSelect()
    // walks outward from selectedIndex for as long as the momentum's resting
    // position keeps getting closer (_getClosestResting loops `n += ±1` with no
    // step limit), so a forceful swipe can resolve to selectedIndex ± 2 or further.
    // The returned index is deliberately un-modulo'd, so with wrapAround a +2 from
    // slide 1 of 3 arrives at 0 instead of the adjacent 2 — and with only two
    // slides it reads as a swipe that snaps back to the banner you started on.
    //
    // Clamp the destination to a single step. Overriding dragEndRestingSelect keeps
    // the correction inside Flickity's dragEnd(), before select() runs, so 'change',
    // the page dots and _syncTabindex only ever observe the clamped index — no
    // visible overshoot that gets fixed up afterwards. Passing selectedIndex ± 1
    // (which may be -1 or slides.length) is the exact value a normal one-slide drag
    // produces, so wrapAround positioning stays on Flickity's supported path.
    _forceSingleStepPaging() {
      const flkty = this.flkty;
      if (!flkty) return;

      const restingSelect = flkty.dragEndRestingSelect.bind(flkty);
      let dragStartIndex = null;

      flkty.on('dragStart', () => {
        dragStartIndex = flkty.selectedIndex;
      });

      flkty.dragEndRestingSelect = () => {
        const target = restingSelect();
        // Fall back to selectedIndex if dragStart never fired — Flickity does not
        // move the selection mid-drag, so both are the pre-drag index.
        const from = dragStartIndex === null ? flkty.selectedIndex : dragStartIndex;
        dragStartIndex = null;

        // Safety net rather than the main mechanism: the aperiodic spring above keeps
        // the momentum projection near one slide, so this rarely triggers. The spring
        // absorbs whatever momentum is left over without overshooting, so the leftover
        // velocity does not need zeroing — doing that only made the start sluggish.
        return Math.min(Math.max(target, from - 1), from + 1);
      };
    }

    _syncTabindex() {
      if (!this.flkty) return;
      this.querySelectorAll('.banner-slider__slide').forEach((slide, i) => {
        const selected = i === this.flkty.selectedIndex;
        slide.querySelectorAll('a, button').forEach((el) => {
          el.setAttribute('tabindex', selected ? '0' : '-1');
        });
      });
    }

    destroySlider() {
      this.flkty?.destroy();
      this.flkty = null;
    }

    disconnectedCallback() {
      if (this._onLoad) {
        window.removeEventListener('load', this._onLoad);
        this._onLoad = null;
      }
      this.cleanupBreakpoint?.();
      this.destroySlider();
    }
  }

  customElements.define('banner-slider', BannerSlider);
})();
